import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { SOLUTIONS } from "@/data/content";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";
import { PageAtmosphere, FloatChip, EyebrowPill } from "@/components/site/PageAtmosphere";

const MESHES = ["", "mesh-b", "mesh-c", "", "mesh-b"];

export default function Solutions() {
    const { openDemo } = useOutletContext();

    return (
        <div className="bg-warm">
            {/* HERO */}
            <section className="relative overflow-hidden pt-20 pb-16 bg-warm">
                <PageAtmosphere />
                <FloatChip icon="fa-solid fa-shield-halved" label="SOC 2 compliant" accent="#2E7D6B" delay={0.2} className="absolute top-24 left-[6%]" />
                <FloatChip icon="fa-solid fa-language" label="22+ languages" accent="#D4B542" delay={0.5} className="absolute top-40 right-[7%]" />
                <FloatChip icon="fa-solid fa-bolt" label="Live in under 5 days" accent="#5FA88A" delay={0.8} className="absolute bottom-6 left-[14%]" />
                <div className="container-x relative z-10 max-w-4xl mx-auto text-center">
                    <Reveal className="mb-6">
                        <EyebrowPill label="Solutions" accent="#2E7D6B" testid="solutions-eyebrow" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="heading-hero text-5xl sm:text-6xl lg:text-7xl">
                            One agentic platform.
                            <br />
                            <span className="muted">A family of specialist bots.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 text-intro text-slate-600 max-w-2xl mx-auto text-base md:text-lg">
                            Each NyraAI solution follows the same pattern — retrieve grounded knowledge, reason
                            with your rules, recommend with confidence, and execute only after a human approves.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                            <button onClick={openDemo} className="pill-dark" data-testid="solutions-hero-book">
                                Book a demo
                            </button>
                            <a href="#grid" className="pill-ghost">
                                See all solutions
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* GRID — alternating cards in mesh frames */}
            <section id="grid" className="pb-20 bg-warm">
                <div className="container-x">
                    <StaggerGroup className="grid md:grid-cols-2 gap-6">
                        {SOLUTIONS.map((s, i) => (
                            <StaggerChild key={s.slug}>
                                <Link
                                    to={`/solutions/${s.slug}`}
                                    className="block h-full group"
                                    data-testid={`solution-card-${s.slug}`}
                                >
                                    <motion.div
                                        whileHover={{ y: -4 }}
                                        transition={{ duration: 0.3 }}
                                        className={`mesh-frame ${MESHES[i % MESHES.length]} min-h-[280px] flex flex-col justify-between`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div
                                                className="icon-sticker w-14 h-14"
                                                style={{ "--st-color": s.accent }}
                                            >
                                                <i className={`${s.icon} text-xl`} />
                                            </div>
                                            <div className="glass-card px-3 py-1 text-[10px] tracking-[0.15em] uppercase text-slate-700" style={{ borderRadius: 999 }}>
                                                {s.eyebrow}
                                            </div>
                                        </div>
                                        <div>
                                            <h2
                                                className="heading-section text-2xl md:text-3xl text-slate-900 mb-2"
                                            >
                                                {s.title}
                                            </h2>
                                            <p className="text-slate-700 text-sm leading-relaxed max-w-md">
                                                {s.headline}
                                            </p>
                                            <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-900 font-medium">
                                                {s.cta}
                                                <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                                            </div>
                                        </div>
                                    </motion.div>
                                </Link>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-warm">
                <div className="container-x text-center max-w-3xl mx-auto">
                    <Reveal>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            Not sure which one fits?
                            <br />
                            <span className="muted">We&apos;ll find it with you.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <button onClick={openDemo} className="pill-dark" data-testid="solutions-footer-book">
                                Book a demo
                            </button>
                            <Link to="/contact" className="pill-ghost">
                                Talk to sales
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
